import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { MatDialog } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { select, Store } from '@ngrx/store';
import { BalanceEntry } from 'app/api/balance.api.service';
import { AppState } from 'app/reducers';
import { AmplifyService } from 'aws-amplify-angular';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap, map, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BalanceQuery } from './balance.actions';
import {
    selectBalanceSorted,
    selectBalanceLoading,
    selectBalanceQuery
} from './balance.selectors';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import { AgGridAngular  } from '@ag-grid-community/angular';
import { agGridLocale, EurCurrencyCellRenderer } from 'app/utils/ag-grid.locale';

@Component({
    selector: 'balance',
    templateUrl: './balance.component.html',
    styleUrls: ['./balance.component.scss'],
    animations: fuseAnimations,
    encapsulation: ViewEncapsulation.None
})
export class BalanceComponent implements OnInit, OnDestroy, AfterViewInit {
    public gridApi;
    public gridColumnApi;

    public columnDefs;
    public defaultColDef;
    public sideBar;
    public statusBar;
    public rowBuffer;
    public localeText;
    public modules;

    keyUp = new Subject<KeyboardEvent>();

    rowData$: Observable<BalanceEntry[]>;
    loading$: Observable<boolean>;

    @ViewChild('agGrid', { static: false }) agGrid: AgGridAngular;

    // Protected
    protected logger: any;

    // Private
    private _unsubscribeAll: Subject<any>;
    private query: BalanceQuery;

    constructor(
        protected amplifyService: AmplifyService,
        protected router: Router,
        protected activatedRoute: ActivatedRoute,
        protected store: Store<AppState>,
        protected dialog: MatDialog
    ) {
        this.logger = this.amplifyService.logger('BalanceComponent');
        this._unsubscribeAll = new Subject();
        this.modules = AllModules;
        this.localeText = agGridLocale.fr;
        this.columnDefs = [
            { headerName: 'Clé', field: 'key' },
            { headerName: 'Compte', field: 'account' },
            { headerName: 'Libellé', field: 'entryLabel' },
            { headerName: 'Débit', field: 'debit', type: 'numericColumn', cellRenderer: EurCurrencyCellRenderer },
            { headerName: 'Crédit', field: 'credit', type: 'numericColumn', cellRenderer: EurCurrencyCellRenderer },
            { headerName: 'Utilisé ?',
                valueGetter: '(data.debit > 0 || data.credit > 0) ? "Oui" : "Non"',
            }
        ];
        this.defaultColDef = {
            enableValue: true,
            enableRowGroup: true,
            enablePivot: true,
            sortable: true,
            filter: true,
            resizable: true
        };
        this.sideBar = {
            toolPanels: [
                {
                    id: 'columns',
                    labelDefault: 'Colonnes',
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel'
                },
                {
                    id: 'filters',
                    labelDefault: 'Filtres',
                    labelKey: 'filters',
                    iconKey: 'filter',
                    toolPanel: 'agFiltersToolPanel'
                }
            ]
            // defaultToolPanel: 'columns',
            // hiddenByDefault: true
        };
        this.statusBar = {
            statusPanels: [
                {
                    statusPanel: 'agTotalRowCountComponent',
                    align: 'left'
                },
                {
                    statusPanel: 'agAggregationComponent',
                    // statusPanelParams: {
                    //     // only show count and sum ('min', 'max', 'avg' won't be shown)
                    //     aggFuncs: ['count', 'sum']
                    // }
                },
                { statusPanel: 'agSelectedRowCountComponent' },
                { statusPanel: 'agFilteredRowCountComponent' }
            ]
        };
        // this.rowBuffer = 500;
    }

    ngOnInit(): void {
        this.loading$ = this.store.pipe(select(selectBalanceLoading));

        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(selectBalanceQuery),
                tap(query => {
                    this.query = query;
                    // Set Url
                    this.setUrlFromQuery(query);
                })
            )
            .subscribe();

        this.keyUp
            .pipe(
                takeUntil(this._unsubscribeAll),
                map((event: any) => event.target.value),
                debounceTime(250),
                distinctUntilChanged(),
                tap(search => this.gridApi.setQuickFilter(search))
            ).subscribe();

        this.rowData$ = this.store.pipe(select(selectBalanceSorted));
    }

    onGridReady(params): void {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;
    }

    public clearSearch(): void {
        this.gridApi.setQuickFilter('');
    }

    private setUrlFromQuery(query: BalanceQuery): void {
        const queryParams: any = {
            key: query.key ? JSON.stringify(query.key) : null,
            filter: query.filter ? JSON.stringify(query.filter) : null
        };
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams
        });
    }

    onFirstDataRendered(params): void {
        this.autoSizeAll(params);
    }

    private autoSizeAll(params): void {
        const allColumnIds = params.columnApi
            .getAllColumns()
            .map(column => column.colId);
        params.columnApi.autoSizeColumns(allColumnIds);
    }

    ngAfterViewInit(): void {
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

}
